"use client";

import React from "react";
import WebsiteLayout from "../../components/LandingPageWrapper";
import Metadata from "../../components/Metadata";

interface SectionData {
  id: string;
  title: string;
  children: React.ReactNode;
}

type DynamicElementProps = 
  { element: "p" | "h3"; value: string; classNames?: string }
  | { element: "ul"; value: readonly string[]; classNames?: string };

const sections = [
  {
    id: "intro",
    title: "",
    children: [
      {
        element: "p",
        value:
          'Safelto ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Safelto platform, including our mobile application, website, and related services (collectively, the "Services").',
      },
      { element: "p", value: "By using Safelto, you agree to the practices described in this Privacy Policy." },
    ],
  },
  {
    id: "collect",
    title: "1. Information We Collect",
    children: [
      { element: "p", value: "We may collect the following categories of information:" },
      { element: "h3", value: "a. Personal Information" },
      { element: "ul", classNames: "list-disc pl-6 space-y-2",
        value: [
          "Full name",
          "Phone number",
          "Email address",
          "Residential address",
          "Profile photo (optional)",
          "Government-issued ID (for verification, where required)",
        ],
      },
      { element: "h3", value: "b. Community & Residency Information" },
      { element: "ul", classNames: "list-disc pl-6 space-y-2",
        value: [
          "Estate or community name",
          "House or unit number",
          "Residency status (resident, guest, landlord, staff, etc.)",
          "Visitor access logs",
        ],
      },
      { element: "h3", value: "c. Payment & Billing Information" },
      { element: "p", value: "Safelto does not act as a wallet or custodian of funds. However, we may collect:" },
      { element: "ul", classNames: "list-disc pl-6 space-y-2",
        value: [
          "Payment references",
          "Transaction IDs",
          "Levy status (paid/unpaid)",
          "Bank verification details",
        ],
      },
      { element: "p", value: "All actual payments are processed through third-party payment providers." },
      { element: "h3", value: "d. Usage & Device Information" },
      { element: "ul", classNames: "list-disc pl-6 space-y-2",
        value: [
          "IP address",
          "Device type",
          "Operating system",
          "App version",
          "Log data",
          "Date and time of access",
        ],
      },
    ],
  },
  {
    id: "usage",
    title: "2. How We Use Your Information",
    children: [
      { element: "ul", classNames: "list-disc pl-6 space-y-2",
        value: [
          "Provide and operate Safelto’s core services",
          "Manage access control and visitor permissions",
          "Enforce levy compliance",
          "Enable communication between residents and community management",
          "Improve and optimize the platform",
          "Detect and prevent fraud or abuse",
          "Comply with legal and regulatory obligations",
        ],
      },
    ],
  },
  {
    id: "legal",
    title: "3. Legal Basis for Processing",
    children: [
      { element: "ul", classNames: "list-disc pl-6 space-y-2",
        value: [
          "Your consent",
          "Performance of a contract",
          "Compliance with legal obligations",
          "Legitimate interests (such as security and service improvement)",
        ],
      },
    ],
  },
  {
    id: "sharing",
    title: "4. How We Share Your Information",
    children: [
      { element: "h3", value: "a. Community Administrators" },
      { element: "p", value: "Estate or building managers may access relevant data to manage residents, visitors, and levies." },
      { element: "h3", value: "b. Service Providers" },
      { element: "p", value: "We may share information with third-party vendors that help us operate Safelto (e.g., hosting, analytics, payment processors)." },
      { element: "h3", value: "c. Legal Authorities" },
      { element: "p", value: "If required by law, court order, or to protect our legal rights." },
      { element: "p", classNames: "font-medium", value: "We do not sell your personal data." },
    ],
  },
  {
    id: "retention",
    title: "5. Data Retention",
    children: [
      { element: "ul", classNames: "list-disc pl-6 space-y-2", value: ["Fulfill the purposes outlined in this policy", "Comply with legal obligations", "Resolve disputes", "Enforce our agreements"] },
    ],
  },
  {
    id: "security",
    title: "6. Data Security",
    children: [
      {
        element: "p",
        value: "We implement reasonable administrative, technical, and physical safeguards to protect your data. However, no system is 100% secure, and we cannot guarantee absolute security.",
      },
    ],
  },
  {
    id: "rights",
    title: "7. Your Rights",
    children: [
      { element: "ul", classNames: "list-disc pl-6 space-y-2",
        value: ["Access your personal data", "Correct inaccurate data", "Delete your data", "Object to processing", "Withdraw consent", "Request data portability"],
      },
      { element: "p", value: "You can exercise these rights by contacting us." },
    ],
  },
  {
    id: "cookies",
    title: "8. Cookies & Tracking Technologies",
    children: [
      { element: "p", value: "Safelto may use cookies and similar technologies to enhance user experience, analyze trends, and improve our services." },
    ],
  },
  {
    id: "children",
    title: "9. Children’s Privacy",
    children: [
      { element: "p", value: "Safelto is not intended for children under 13. We do not knowingly collect personal information from children." },
    ],
  },
  {
    id: "thirdparty",
    title: "10. Third-Party Links",
    children: [
      { element: "p", value: "Our Services may contain links to third-party websites. We are not responsible for their privacy practices." },
    ],
  },
  {
    id: "changes",
    title: "11. Changes to This Policy",
    children: [
      { element: "p", value: "We may update this Privacy Policy from time to time. We will notify users of significant changes." },
    ],
  },
  {
    id: "contact",
    title: "12. Contact Us",
    children: [
      { element: "p", value: "If you have questions about this Privacy Policy or your data, contact us at:" },
      { element: "p", classNames: "font-medium", value: "Safelto Support" },
      { element: "p", value: "Email: safelto.app@gmail.com" },
      { element: "p", value: "Phone: +2348066152527" },
    ],
  },
] as const;


export default function PrivacyPolicyPage() {
  const handlePrint = () => window.print();

  const privacyMetadata = {
    title: 'Privacy Policy - Safelto',
    description: 'Safelto Privacy Policy - Learn how we collect, use, and protect your personal information. Understand your rights and our commitment to data security.',
    keywords: ['privacy policy', 'data protection', 'personal information', 'security', 'terms of service'],
    image: '/assets/images/safelto-big.png',
    url: 'https://safelto.com/privacy',
    type: 'website' as 'website'
  }

  return (
    <div>
      <Metadata {...privacyMetadata} />
      <WebsiteLayout>
          {/* Main Content */}
        <div className="w-full max-w-5xl mx-auto flex flex-col items-center gap-3 md:gap-3 mt-12 lg:mt-20">
          <main className="bg-white rounded-2xl shadow-md p-8 z-10 text-sm leading-5">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-2xl font-bold">Safelto Privacy Policy</h1>
                <p className="text-sm text-gray-500">Last updated: January 10, 2026</p>
              </div>
              <button onClick={handlePrint} className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-100">
                Download / Print
              </button>
            </div>

            {sections.map((section) => (
              <Section key={section.id} id={section.id} title={section.title}>
                {section.children.map((child, index) => (
                  <DynamicElement key={`${section.id}-${index}`} {...child} />
                ))}
              </Section>
            ))}
          </main>
        </div>
      </WebsiteLayout>
    </div>
  );
}

function Section({ id, title, children }: SectionData) {
  return (
    <section id={id} className="mb-10 scroll-mt-24">
      <h2 className="text-xl font-semibold mb-3">{title}</h2>
      <div className="text-gray-700 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

function DynamicElement(props: DynamicElementProps) {
  switch (props.element) {
    case "p":
      return <p className={props.classNames}>{props.value}</p>;
    case "h3":
      return <h3 className={`font-semibold mt-4 ${props.classNames ?? ""}`}>{props.value}</h3>;
    case "ul": {
      const classes = props.classNames ?? "list-disc pl-6 space-y-2";
      return (
        <ul className={classes}>
          {props.value.map((item, i) => (
            <li key={i} className="leading-4">{item}</li>
          ))}
        </ul>
      );
    }
    default:
      return <></>;
  }
}
